import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { streamsAPI } from '../../services/api';
import {
  ACTIVITY_WINDOW_LABEL,
  ACTIVITY_CARD_BODY_CLASS,
  ActivityStat,
  PeriodSelect,
} from '../activity/ActivityCardShared';
import AvatarGroup from '../ui/AvatarGroup';
import { Waves, GitFork, Users, GitCommit, GitPullRequest, ChevronRight } from 'lucide-react';

/**
 * Organisation-level stream summary: loads card-stats per selected period on this card.
 *
 * @param {{ orgId: string, stream: { id: string, name?: string, slug?: string } }} props
 */
export default function OrganisationStreamCard({ orgId, stream }: any) {
  const [timeframe, setTimeframe] = useState('month');
  const [row, setRow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [members, setMembers] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId || !stream?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    streamsAPI
      .getCardStats(orgId, stream.id, { timeframe })
      .then((card) => {
        if (!cancelled) setRow(card);
      })
      .catch(() => {
        if (!cancelled) {
          setRow(null);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, stream.id, timeframe]);

  useEffect(() => {
    const memberCount = row?.memberCount ?? 0;
    if (!orgId || !stream?.id || memberCount === 0) {
      setMembers([]);
      return undefined;
    }
    let cancelled = false;
    streamsAPI
      .getMembers(orgId, stream.id)
      .then((list) => {
        if (!cancelled) setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, stream?.id, row?.memberCount]);

  const display = row;
  const tf = timeframe === 'week' || timeframe === 'year' ? timeframe : 'month';
  const windowLabel = ACTIVITY_WINDOW_LABEL[tf];
  const projects = Array.isArray(display?.projects) ? display.projects : [];
  const commitCount = display?.commitCount ?? 0;
  const activeContributorCount = display?.activeContributorCount ?? 0;
  const mergedPullRequestCount = display?.mergedPullRequestCount ?? 0;
  const name = display?.name ?? stream?.name ?? 'Stream';

  return (
    <article className="card-mdl flex flex-col h-full min-h-[260px] overflow-hidden">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-vivid-cyan-blue/20 flex items-center justify-center ring-1 ring-vivid-cyan-blue/25">
          <Waves className="w-5 h-5 text-vivid-cyan-blue" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <h3 className="font-semibold text-slate-100 truncate text-base leading-snug" title={name}>
            {name}
          </h3>
          {members.length > 0 ? <AvatarGroup members={members} max={4} size="xs" /> : null}
        </div>
        <Link
          to={`/streams/${stream.id}`}
          className="btn-icon-mdl btn-icon-mdl-sm flex-shrink-0"
          aria-label={`Open stream ${name}`}
          title={`Open stream ${name}`}
        >
          <ChevronRight className="w-5 h-5" aria-hidden />
        </Link>
      </div>

      <div className={ACTIVITY_CARD_BODY_CLASS}>
        {loadError && <p className="text-amber-300">Could not load activity for this stream.</p>}

        <div>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Activity
            </p>
            <PeriodSelect
              id={`stream-card-timeframe-${stream.id}`}
              value={timeframe}
              onChange={setTimeframe}
              disabled={loading}
            />
          </div>
          <div
            className={`grid grid-cols-3 gap-2 ${loading ? 'opacity-60 pointer-events-none' : ''}`}
            aria-busy={loading}
          >
            <ActivityStat
              icon={GitCommit}
              iconClass="text-vivid-cyan-blue"
              value={commitCount}
              label="Commits"
              title={`Commits (${windowLabel})`}
            />
            <ActivityStat
              icon={Users}
              iconClass="text-vivid-green-cyan"
              value={activeContributorCount}
              label="Contributors"
              title={`Active contributors (${windowLabel})`}
            />
            <ActivityStat
              icon={GitPullRequest}
              iconClass="text-luminous-vivid-amber"
              value={mergedPullRequestCount}
              label="Merged PRs"
              title={`Merged pull requests (${windowLabel})`}
            />
          </div>
        </div>

        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Projects
          </p>
          {loading && !display ? (
            <p className="text-sm text-slate-400">Loading projects…</p>
          ) : projects.length === 0 ? (
            <p className="text-sm text-slate-400">No projects in this stream yet.</p>
          ) : (
            <ul className="rounded-xl border border-dark-border divide-dark-elevated overflow-hidden bg-dark-surface/40/30">
              {projects.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/streams/${stream.id}/projects/${p.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-slate-100 hover:bg-dark-elevated/80 transition-colors group"
                  >
                    <span className="font-medium truncate min-w-0 group-hover:text-vivid-red transition-colors">
                      {p.name}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400 tabular-nums shrink-0">
                      <GitFork className="w-3.5 h-3.5 opacity-70" aria-hidden />
                      {p.repositoryCount ?? 0}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}
